/**
 * Records marketing touchpoints in localStorage.
 *
 * This module makes no consent decision. Whoever loads the browser bundle
 * decides when it may run and calls clear() when stored data must go.
 */

export const STORAGE_KEY = "schulte_marketing_touchpoints_v1";
export const TRACKER_VERSION = 1 as const;
export const MAX_HISTORY = 20;
/** A reload or back-navigation to the same campaign URL within this window is not a new touchpoint. */
export const REPEAT_WINDOW_MS = 30 * 60 * 1000;

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
export type UtmKey = (typeof UTM_KEYS)[number];

/**
 * Click identifiers appended by ad platforms. Extend only with approved parameters.
 * gclid/gbraid/wbraid/dclid Google, msclkid Microsoft, fbclid Meta, li_fat_id LinkedIn, ttclid TikTok, twclid X.
 */
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "li_fat_id", "ttclid", "twclid"] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

const MAX_VALUE_LENGTH = 256;
const MAX_URL_LENGTH = 2048;

export interface Touchpoint {
  /** ISO 8601 timestamp. */
  at: string;
  /** Page origin and path without query string or fragment. */
  page: string;
  /** Referrer origin and path, or null when empty or same-site. */
  referrer: string | null;
  utm: Partial<Record<UtmKey, string>>;
  clickIds: Partial<Record<ClickIdKey, string>>;
}

export interface TouchpointRecord {
  version: typeof TRACKER_VERSION;
  firstSeenAt: string;
  lastSeenAt: string;
  landingPage: string;
  first: Touchpoint;
  last: Touchpoint;
  /** Newest last. Bounded by MAX_HISTORY. */
  history: Touchpoint[];
}

/** Minimal subset of the Web Storage API so tests can inject a fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PageVisit {
  /** Full current URL including query string. */
  url: string;
  /** document.referrer, may be empty. */
  referrer: string;
  now: Date;
}

export function read(storage: StorageLike): TouchpointRecord | null {
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clear(storage: StorageLike): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}

/**
 * Turns the current page visit into a touchpoint, or null when the visit
 * carries no campaign information and comes from the same site.
 */
export function touchpointFromVisit(visit: PageVisit): Touchpoint | null {
  const current = parseUrl(visit.url);
  if (!current) return null;

  const utm: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = current.searchParams.get(key);
    if (value) utm[key] = truncate(value, MAX_VALUE_LENGTH);
  }

  const clickIds: Partial<Record<ClickIdKey, string>> = {};
  for (const key of CLICK_ID_KEYS) {
    const value = current.searchParams.get(key);
    if (value) clickIds[key] = truncate(value, MAX_VALUE_LENGTH);
  }

  const referrerUrl = visit.referrer ? parseUrl(visit.referrer) : null;
  const externalReferrer = referrerUrl && referrerUrl.host !== current.host ? stripQuery(referrerUrl) : null;

  const hasCampaignData = Object.keys(utm).length > 0 || Object.keys(clickIds).length > 0;
  if (!hasCampaignData && !externalReferrer) return null;

  return {
    at: visit.now.toISOString(),
    page: stripQuery(current),
    referrer: externalReferrer,
    utm,
    clickIds,
  };
}

/**
 * Updates the stored record for the current visit and returns it.
 * The first visit always creates a record, even without campaign data,
 * so the landing page is known. Later visits only add a touchpoint when
 * they carry campaign parameters or come from another site, and not when
 * they repeat the previous touchpoint within REPEAT_WINDOW_MS (page reload).
 */
export function record(storage: StorageLike, visit: PageVisit): TouchpointRecord | null {
  const existing = read(storage);
  const touchpoint = touchpointFromVisit(visit) ?? (existing ? null : plainTouchpoint(visit));
  if (!touchpoint) return existing;
  if (existing && isRepeat(existing.last, touchpoint)) return existing;

  const next: TouchpointRecord = existing
    ? {
        ...existing,
        lastSeenAt: touchpoint.at,
        last: touchpoint,
        history: [...existing.history, touchpoint].slice(-MAX_HISTORY),
      }
    : {
        version: TRACKER_VERSION,
        firstSeenAt: touchpoint.at,
        lastSeenAt: touchpoint.at,
        landingPage: touchpoint.page,
        first: touchpoint,
        last: touchpoint,
        history: [touchpoint],
      };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota exceeded or storage blocked. Return the in-memory record anyway.
  }
  return next;
}

function isRepeat(previous: Touchpoint, next: Touchpoint): boolean {
  const elapsed = Date.parse(next.at) - Date.parse(previous.at);
  if (!(elapsed >= 0 && elapsed < REPEAT_WINDOW_MS)) return false;
  return (
    previous.page === next.page &&
    previous.referrer === next.referrer &&
    sameEntries(previous.utm, next.utm) &&
    sameEntries(previous.clickIds, next.clickIds)
  );
}

function sameEntries(a: Record<string, string | undefined>, b: Record<string, string | undefined>): boolean {
  const keysA = Object.keys(a).filter((k) => a[k] !== undefined);
  const keysB = Object.keys(b).filter((k) => b[k] !== undefined);
  return keysA.length === keysB.length && keysA.every((k) => a[k] === b[k]);
}

function plainTouchpoint(visit: PageVisit): Touchpoint | null {
  const current = parseUrl(visit.url);
  if (!current) return null;
  return { at: visit.now.toISOString(), page: stripQuery(current), referrer: null, utm: {}, clickIds: {} };
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function stripQuery(url: URL): string {
  return truncate(`${url.origin}${url.pathname}`, MAX_URL_LENGTH);
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function isRecord(value: unknown): value is TouchpointRecord {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v["version"] === TRACKER_VERSION &&
    typeof v["firstSeenAt"] === "string" &&
    typeof v["lastSeenAt"] === "string" &&
    typeof v["landingPage"] === "string" &&
    isTouchpoint(v["first"]) &&
    isTouchpoint(v["last"]) &&
    Array.isArray(v["history"]) &&
    v["history"].every(isTouchpoint)
  );
}

function isTouchpoint(value: unknown): value is Touchpoint {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v["at"] === "string" &&
    typeof v["page"] === "string" &&
    (v["referrer"] === null || typeof v["referrer"] === "string") &&
    typeof v["utm"] === "object" &&
    v["utm"] !== null &&
    typeof v["clickIds"] === "object" &&
    v["clickIds"] !== null
  );
}
